import { getLocalTimeZone } from "@internationalized/date";
import { fetcher } from "@website/fetcher/fetcher";
import { getToken } from "@website/hooks/auth-manager";
import { getTracking, setTracking } from "@website/store/tracking";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { Bus } from "@website/tracking/types/bus";
import { Core } from "@website/tracking/types/core";
import { DomFlight } from "@website/tracking/types/dom_flight";
import { DomHotel } from "@website/tracking/types/dom_hotel";
import { Experiment } from "@website/tracking/types/experiment";
import { IntFlight } from "@website/tracking/types/int_flight";
import { IntHotel } from "@website/tracking/types/int_hotel";
import { Tour } from "@website/tracking/types/tour";
import { Train } from "@website/tracking/types/train";
import { Villa } from "@website/tracking/types/villa";
import { getChannel } from "@website/utils/channel/get-channel";
import { getCookie, setCookie } from "@website/utils/cookie";
import { deferToIdle } from "@website/utils/defer-to-idle";
import { getDeviceInfo } from "@website/utils/device-info";
import { isFilled } from "@website/utils/is-filled";
import { isSSR } from "@website/utils/is-ssr";
import { uuid } from "@website/utils/uuid";
import jwtDecode from "jwt-decode";
import clamp from "lodash-es/clamp";
import debounce from "lodash-es/debounce";
import isEmpty from "lodash-es/isEmpty";
import isFunction from "lodash-es/isFunction";
import mapValues from "lodash-es/mapValues";
import toString from "lodash-es/toString";
import values from "lodash-es/values";

export type ApplicationNameType =
  | "hotel-domestic"
  | "villa"
  | "tour"
  | "hotel-international";
export type ProductType =
  | "dom-hotel"
  | "int-hotel"
  | "dom-flight"
  | "int-flight"
  | "villa"
  | "tour"
  | "bus"
  | "train"
  | "core"
  | "accommodation";

export type ProductPropertyMap = {
  "int-flight": IntFlight;
  "dom-flight": DomFlight;
  "int-hotel": IntHotel;
  "dom-hotel": DomHotel;
  villa: Villa;
  tour: Tour;
  core: Core;
  bus: Bus;
  train: Train;
  accommodation: Core;
};

let timeout: ReturnType<typeof setTimeout> | null = null;
let isBeaconListenersRegistered = false;
const GOOGLE_AD_PARAMETERS = ["gclid", "gbraid", "wbraid"] as const;

const getGoogleAdCookieName = (parameter: string) => `ads_${parameter}`;

const getGoogleAdValue = (parameter: string, parameters: URLSearchParams) =>
  toString(
    parameters.get(parameter) || getCookie(getGoogleAdCookieName(parameter))
  );

const persistGoogleAds = (parameters: URLSearchParams) => {
  for (const parameter of GOOGLE_AD_PARAMETERS) {
    const value = parameters.get(parameter);

    if (isFilled(value)) {
      setCookie(getGoogleAdCookieName(parameter), value, {
        path: "/",
        maxAge: 30 * 24 * 60 * 60 // 30 days
      });
    }
  }
};

const getUserTrackingQueue = () => {
  const trackingState = getTracking();
  const queue =
    trackingState.queue?.filter(
      (item: { aid?: string }) => "aid" in item && !isEmpty(item)
    ) ?? [];

  const hasEvent = queue.length > 0 && Boolean(trackingState.sid);

  return { queue, hasEvent };
};

const clearTrackingQueue = () =>
  setTracking((previous) => {
    const queue = previous.queue ?? [];

    if (queue.length === 0) {
      return previous;
    }
    return {
      ...previous,
      queue: queue.length > 0 && previous.sid ? [] : queue
    };
  });

const sendEventsWithBeacon = () => {
  if (isSSR() || typeof navigator === "undefined") return false;
  if (!("sendBeacon" in navigator)) return false;

  const { queue, hasEvent } = getUserTrackingQueue();
  if (!hasEvent || queue.length === 0) return false;

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL_USER_TRACKING;
  if (!baseUrl) return false;

  try {
    const url = new URL("/ut/v3/public", baseUrl);
    url.searchParams.set("token", getChannel());

    const success = navigator.sendBeacon(
      url.toString(),
      new Blob([JSON.stringify(queue)], {
        type: "application/json"
      })
    );

    if (success) {
      clearTrackingQueue();
    }

    return success;
  } catch {
    return false;
  }
};

const flushTrackingQueue = () => {
  sendEventsWithBeacon();
};

const handleBeaconFlush = () => flushTrackingQueue();

const ensureBeaconFlushListeners = () => {
  if (isSSR() || isBeaconListenersRegistered) return;

  window.addEventListener("pagehide", handleBeaconFlush);
  window.addEventListener("beforeunload", handleBeaconFlush);
  //TODO: add removeEventListeners if needed later
  isBeaconListenersRegistered = true;
};

export const mapProductForUserTracking = (product: ApplicationNameType) =>
  (({
    "hotel-domestic": "dom-hotel",
    "hotel-international": "int-hotel",
    villa: "villa",
    tour: "tour"
  })[product] || "core") as ProductType;

const sendEvents = <K extends keyof ProductPropertyMap>(
  data: { product: K } & Partial<ProductPropertyMap[K]> & Partial<Experiment>,
  exponent = 1000,
  delay = 1000
) => {
  const product = isFunction(data.product) ? data.product() : data.product;
  const { queue: userTrackingQueue, hasEvent } = getUserTrackingQueue();
  if (!hasEvent || userTrackingQueue.length === 0) {
    timeout = setTimeout(
      () => sendEvents(data, exponent, delay),
      clamp(delay, 10_000)
    );
    return;
  }
  clearTrackingQueue();
  return fetcher({
    url: `${process.env.NEXT_PUBLIC_BASE_URL_USER_TRACKING}/ut/v3/public`,
    method: "POST",
    data: userTrackingQueue,
    params: {
      token: getChannel()
    },
    headers: {
      product: String(product)
    }
  }).finally(() => {
    timeout = setTimeout(
      () => sendEvents(data, delay + exponent, exponent),
      clamp(delay, 10_000)
    );
  });
};

const collectUrlChanges = <K extends keyof ProductPropertyMap>(
  data: { product: K } & Partial<ProductPropertyMap[K]> & Partial<Experiment>
) => {
  const addEvent = debounce(() => {
    const urlPath = window.location.origin + window.location.pathname;
    const { previousUrl } = getTracking();
    const pageViewed = previousUrl !== urlPath;
    if (pageViewed) {
      addEventToTracking(USER_TRACKING_EVENTS.PAGE_VIEWED, data);
      setTracking((previous) => ({
        ...previous,
        previousUrl: pageViewed ? urlPath : previous.previousUrl
      }));
    }
  }, 100);

  const { pushState } = window.history;
  const { replaceState } = window.history;

  window.history.pushState = (state, title, url) => {
    const result = pushState.apply(window.history, [state, title, url]);
    deferToIdle(() => addEvent());
    return result;
  };

  window.history.replaceState = (state, title, url) => {
    const result = replaceState.apply(window.history, [state, title, url]);
    deferToIdle(() => addEvent());
    return result;
  };

  window.addEventListener("popstate", () => deferToIdle(addEvent));
  window.addEventListener("hashchange", () => deferToIdle(addEvent));

  deferToIdle(addEvent);
};

const generateQueue = <K extends keyof ProductPropertyMap>(
  event: USER_TRACKING_EVENTS,
  data: { product: K } & Partial<ProductPropertyMap[K]> & Partial<Experiment>,
  state: Omit<ReturnType<typeof getTracking>, "queue">
) => {
  const query = new URLSearchParams(window.location.search);
  const product = isFunction(data.product) ? data.product() : data.product;
  const device = getDeviceInfo();
  return {
    event,
    product,
    channel: getChannel(),
    uid: state.uid,
    sid: state.sid,
    aid: state.aid,
    mid: uuid(),
    timestamp: new Date().toISOString(),
    sent_at: new Date().toISOString(),
    timezone: getLocalTimeZone(),
    properties: {
      experiments: values(
        mapValues(state.experiment, (value, key) => ({
          experiment_name: toString(key),
          variation_name: toString(value)
        }))
      ),
      filters: Object.entries({
        date_from: query.get("date_from"),
        date_to: query.get("date_to"),
        accommodations: query.get("accommodations"),
        category_ids: query.get("categories"),
        stars: (query.get("stars") || "").replaceAll("-", ","),
        price_from: query.get("price_from"),
        price_to: query.get("price_to"),
        availability: query.get("availability"),
        no_rooms: query.get("no_rooms"),
        adults: query.get("adults"),
        children: query.get("children")
      })
        .filter(([, value]) => isFilled(value))
        .map(([key, value]) => ({ key, value })),
      sorts: [{ key: query.get("order_by") || "selling", value: "asc" }],
      ...(product ? { [product]: data } : {})
    },
    context: {
      app: {
        name: process.env.NEXT_PUBLIC_APP_NAME,
        version: process.env.NEXT_PUBLIC_IMAGE_VERSION
      },
      utm: {
        internal_source: query.get("utm_internal_source"),
        source: query.get("utm_source"),
        medium: query.get("utm_medium"),
        campaign: query.get("utm_campaign"),
        term: query.get("utm_term"),
        content: query.get("utm_content"),
        ref: query.get("utm_ref")
      },
      device: device && {
        id: undefined, // todo
        advertising_id: undefined, // todo
        adtracking_enabled: true,
        manufacturer: device.device.vendor,
        model: device.device.model,
        name: device.device.model,
        type: device.device.type,
        width: window.innerWidth,
        height: window.innerHeight,
        locale: "", // todo
        user_agent: window.navigator?.userAgent,
        os_name: device.os.name,
        os_version: device.os.version,
        browser_name: device.browser.name,
        browser_version: device.browser.version
      },
      network: {
        ip: "" // todo
      },
      page: {
        path: decodeURIComponent(window.location.pathname),
        referrer: decodeURIComponent(document.referrer),
        previous_page: state.previousUrl,
        title: document.title,
        url: decodeURIComponent(window.location.href)
      },
      google: {
        ads: {
          gclid: getGoogleAdValue("gclid", query),
          gbraid: getGoogleAdValue("gbraid", query),
          wbraid: getGoogleAdValue("wbraid", query)
        }
      }
    }
  };
};

export const addEventToTracking = <K extends keyof ProductPropertyMap>(
  event: USER_TRACKING_EVENTS,
  data: { product: K } & Partial<ProductPropertyMap[K]> & Partial<Experiment>
) => {
  if (isSSR()) return;
  const token = getToken()?.stToken;
  setTracking((previous) => {
    const sid =
      previous.sessionExpire <= Date.now() ? uuid() : previous.sid || uuid();
    const state = {
      ...previous,
      sessionExpire: Date.now() + 30 * 60 * 1000,
      sid,
      aid: previous.aid || uuid(),
      uid: token ? jwtDecode<{ sub: string }>(token).sub : ""
    };
    return {
      ...state,
      queue: [
        ...(previous.sessionExpire <= Date.now() || !previous.sessionExpire
          ? [
              generateQueue(
                USER_TRACKING_EVENTS.USER_SESSION_STARTED,
                data,
                state
              )
            ]
          : []),
        ...previous.queue,
        generateQueue(event, data, state)
      ]
    };
  });
};

export const startUserTracking = <K extends keyof ProductPropertyMap>(
  data: { product: K } & Partial<ProductPropertyMap[K]> & Partial<Experiment>
) => {
  if (isSSR()) return;

  persistGoogleAds(new URLSearchParams(window.location.search));

  timeout && clearTimeout(timeout);
  ensureBeaconFlushListeners();

  setTimeout(() => {
    sendEvents(data);
    collectUrlChanges(data);
  }, 500);
};
